import classicEleganceCover from '../assets/images/template_cover/classic-elegance.webp';
import modernRomanceCover from '../assets/images/template_cover/modern-romance.webp';
import rusticGardenCover from '../assets/images/template_cover/rustic-garden.webp';
import kidsPartyFunCover from '../assets/images/template_cover/kids-party-fun.webp';
import elegantMilestoneCover from '../assets/images/template_cover/elegant-milestone.webp';
import fiestaThemeCover from '../assets/images/template_cover/fiesta-theme.webp';
import angelicWhiteCover from '../assets/images/template_cover/angelic-white.webp';
import blessedJourneyCover from '../assets/images/template_cover/blessed-journey.webp';
import professionalBlueCover from '../assets/images/template_cover/professional-blue.webp';
import executiveGoldCover from '../assets/images/template_cover/executive-gold.webp';
import silverAnniversaryCover from '../assets/images/template_cover/silver-wedding.webp';
import goldenYearsCover from '../assets/images/template_cover/golden-years.webp';

export const invitationCategories = [
  {
    value: 'all',
    icon: null,
    label: 'All Templates',
    path: '/invitations',
    headline: 'All digital invitation templates',
    blurb:
      'Browse every ready-made design for weddings, birthdays, christenings, corporate events, and anniversaries.',
  },
  {
    value: 'wedding',
    icon: '💍',
    label: 'Wedding',
    path: '/wedding',
    headline: 'Wedding digital invitations',
    blurb:
      'Elegant RSVP-ready designs for save-the-dates, ceremonies, receptions, and intimate wedding celebrations.',
  },
  {
    value: 'birthday',
    icon: '🎂',
    label: 'Birthday',
    path: '/birthday',
    headline: 'Birthday digital invitations',
    blurb:
      'Playful and polished online invitations for kids parties, milestone birthdays, and themed celebrations.',
  },
  {
    value: 'christening',
    icon: '🕊',
    label: 'Christening',
    path: '/christening',
    headline: 'Christening digital invitations',
    blurb:
      'Meaningful baptism and christening invitation designs that are easy for family and sponsors to view on mobile.',
  },
  {
    value: 'corporate',
    icon: '💼',
    label: 'Corporate',
    path: '/corporate',
    headline: 'Corporate digital invitations',
    blurb:
      'Professional online invitations for seminars, launches, company parties, and executive events.',
  },
  {
    value: 'anniversary',
    icon: '🥂',
    label: 'Anniversary',
    path: '/anniversary',
    headline: 'Anniversary digital invitations',
    blurb:
      'Elegant online invitation templates for silver, golden, and milestone anniversary celebrations.',
  },
];

const categoryPageContent = {
  wedding: {
    eyebrow: 'Wedding digital invitations',
    heading: 'Wedding Digital Invitations for Modern Filipino Celebrations',
    description:
      'Explore elegant wedding digital invitations that are easy to share, mobile-friendly for guests, and ready for RSVP collection.',
    lead:
      'Choose a wedding design that feels polished online and practical for real guest communication. These templates help couples send event details quickly without sacrificing style.',
    highlights: [
      'Designed for Messenger, Facebook, email, and mobile sharing',
      'Ideal for save-the-dates, ceremony invites, and reception reminders',
      'A faster launch than a full custom build while staying personalized',
    ],
    metrics: [
      { label: 'Template pricing', value: 'P200 - P300' },
      { label: 'Custom design upgrade', value: 'P350 - P700' },
      { label: 'Best for', value: 'Ceremony and reception invites' },
    ],
    templateLead:
      'Compare the wedding invitation templates below and choose the layout that matches your ceremony, reception, or save-the-date style.',
    ctaLabel: 'Book a Wedding Invitation',
  },
  birthday: {
    eyebrow: 'Birthday digital invitations',
    heading: 'Birthday Digital Invitations for Kids and Milestone Celebrations',
    description:
      'Browse birthday digital invitations for kids parties, themed celebrations, and milestone birthdays that need a fast, shareable online invite.',
    lead:
      'Birthday invitations usually need quick turnaround and easy guest sharing. These ready-made templates help you send details fast while still matching the celebration theme.',
    highlights: [
      'Works well for kids birthdays, 7th birthdays, debuts, and milestone events',
      'Simple to share with family groups and parent circles on mobile',
      'A practical option when you want a themed invitation without a long wait',
    ],
    metrics: [
      { label: 'Template pricing', value: 'P200 - P250' },
      { label: 'Custom design upgrade', value: 'P350 - P700' },
      { label: 'Best for', value: 'Kids parties and milestone birthdays' },
    ],
    templateLead:
      'Review the birthday invitation templates below to find the best fit for playful themes, elegant milestones, and fiesta-inspired parties.',
    ctaLabel: 'Book a Birthday Invitation',
  },
  christening: {
    eyebrow: 'Christening digital invitations',
    heading: 'Christening and Baptism Digital Invitations for Family Celebrations',
    description:
      'Choose christening digital invitations that make it easy to share ceremony details, reception information, and RSVP instructions with family and sponsors.',
    lead:
      'For baptisms and christenings, clarity matters as much as design. These templates help families send meaningful invitation pages that guests can open on any phone.',
    highlights: [
      'Made for baptism, christening, and blessing celebrations',
      'Clear layout for church, reception, and RSVP details',
      'Soft, elegant styles for faith-centered family events',
    ],
    metrics: [
      { label: 'Template pricing', value: 'P200 - P250' },
      { label: 'Custom design upgrade', value: 'P350 - P700' },
      { label: 'Best for', value: 'Baptisms and christenings' },
    ],
    templateLead:
      'Browse the christening invitation templates below for elegant baptism and faith-focused family celebrations.',
    ctaLabel: 'Book a Christening Invitation',
  },
  corporate: {
    eyebrow: 'Corporate digital invitations',
    heading: 'Corporate Digital Invitations for Professional Events and Launches',
    description:
      'Browse corporate digital invitations for seminars, launches, networking events, and company gatherings that need a cleaner and more professional online presence.',
    lead:
      'Corporate invitations need to look organized, modern, and easy to open on any device. These templates help teams send branded event links without a slow design cycle.',
    highlights: [
      'Suitable for seminars, launches, trainings, and executive events',
      'Professional layouts that keep key event details clear',
      'A faster option for teams that need a presentable invite quickly',
    ],
    metrics: [
      { label: 'Template pricing', value: 'P200 - P300' },
      { label: 'Custom design upgrade', value: 'P350 - P700' },
      { label: 'Best for', value: 'Seminars, launches, and company events' },
    ],
    templateLead:
      'Explore the corporate invitation templates below for business events that need a polished, mobile-ready invitation page.',
    ctaLabel: 'Book a Corporate Invitation',
  },
  anniversary: {
    eyebrow: 'Anniversary digital invitations',
    heading: 'Anniversary Digital Invitations for Milestone Love Stories',
    description:
      'Explore anniversary digital invitations for silver, golden, and milestone celebrations that deserve an elegant online invitation experience.',
    lead:
      'Anniversary celebrations often need a softer, more elegant presentation. These templates help couples and families share details in a format guests can open instantly.',
    highlights: [
      'Built for silver, golden, and milestone anniversary events',
      'Elegant layouts that feel celebratory without being hard to navigate',
      'A simple way to share event details with family across devices',
    ],
    metrics: [
      { label: 'Template pricing', value: 'P250 - P300' },
      { label: 'Custom design upgrade', value: 'P350 - P700' },
      { label: 'Best for', value: 'Silver and golden anniversaries' },
    ],
    templateLead:
      'Compare the anniversary invitation templates below to find a style that fits elegant milestone celebrations.',
    ctaLabel: 'Book an Anniversary Invitation',
  },
};

export const invitationTemplates = [
  {
    id: 1,
    title: 'Classic Elegance',
    category: 'wedding',
    slug: 'classic-elegance',
    image: classicEleganceCover,
    description: 'Timeless and sophisticated wedding invitation with floral accents',
    price: 'P200',
  },
  {
    id: 2,
    title: 'Modern Romance',
    category: 'wedding',
    slug: 'modern-romance',
    image: modernRomanceCover,
    description: 'Contemporary design with minimalist elegance',
    price: 'P250',
  },
  {
    id: 3,
    title: 'Rustic Garden',
    category: 'wedding',
    slug: 'rustic-garden',
    image: rusticGardenCover,
    description: 'Nature-inspired design with botanical elements',
    price: 'P300',
  },
  {
    id: 4,
    title: 'Kids Party Fun',
    category: 'birthday',
    slug: 'kids-party-fun',
    image: kidsPartyFunCover,
    description: "Colorful and playful design for children's parties",
    price: 'P200',
  },
  {
    id: 5,
    title: 'Elegant Milestone',
    category: 'birthday',
    slug: 'elegant-milestone',
    image: elegantMilestoneCover,
    description: 'Sophisticated design for milestone celebrations (18th, 50th, etc.)',
    price: 'P250',
  },
  {
    id: 6,
    title: 'Fiesta Theme',
    category: 'birthday',
    slug: 'fiesta-theme',
    image: fiestaThemeCover,
    description: 'Vibrant Filipino fiesta-inspired celebration',
    price: 'P200',
  },
  {
    id: 7,
    title: 'Angelic White',
    category: 'christening',
    slug: 'angelic-white',
    image: angelicWhiteCover,
    description: 'Pure and heavenly design for baptism celebrations',
    price: 'P200',
  },
  {
    id: 8,
    title: 'Blessed Journey',
    category: 'christening',
    slug: 'blessed-journey',
    image: blessedJourneyCover,
    description: 'Beautiful religious-themed invitation',
    price: 'P250',
  },
  {
    id: 9,
    title: 'Professional Blue',
    category: 'corporate',
    slug: 'professional-blue',
    image: professionalBlueCover,
    description: 'Clean and professional design for business events',
    price: 'P200',
  },
  {
    id: 10,
    title: 'Executive Gold',
    category: 'corporate',
    slug: 'executive-gold',
    image: executiveGoldCover,
    description: 'Premium design for high-profile corporate events',
    price: 'P300',
  },
  {
    id: 11,
    title: 'Silver Anniversary',
    category: 'anniversary',
    slug: 'silver-anniversary',
    image: silverAnniversaryCover,
    description: 'Elegant silver-themed 25th anniversary design',
    price: 'P250',
  },
  {
    id: 12,
    title: 'Golden Years',
    category: 'anniversary',
    slug: 'golden-years',
    image: goldenYearsCover,
    description: 'Luxurious gold-themed celebration invitation',
    price: 'P300',
  },
];

export function getInvitationCategory(value) {
  return invitationCategories.find((category) => category.value === value) || null;
}

export function getCategoryPageContent(value) {
  return categoryPageContent[value] || null;
}

export function getTemplatesByCategory(value) {
  if (!value || value === 'all') return invitationTemplates;
  return invitationTemplates.filter((template) => template.category === value);
}

export function getVisibleInvitationCategories() {
  return invitationCategories.filter((category) => category.value !== 'all');
}